import { useAuthoringEffort, type AuthoringEffort } from '@/features/coaching-effort/useAuthoringEffort';
import { AuthoringEffortControls } from '@/features/coaching-effort/AuthoringEffortControls';
import type { EditCategory } from '@/features/coaching-effort/EffortSession';
import {
  confirmationFactSchema,
  humanConfirmationFact,
  type ConfirmationForm,
} from "./confirmationFact";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/Button";
import { TkticDateInput } from "@/components/ui/TkticDateInput";
import { useAuthStore } from "@/stores/auth";
import { TkticSelect } from "@/components/ui/TkticSelect";
import {
  Composer,
  ComposerPlayerPicker,
  type ComposerHandle,
} from "@/features/coach-review/components/Composer";
import { GoalWritingFields } from "@/features/coach/components/GoalWritingFields";
import { CoachModal } from "@/features/coach/components/CoachModal";
import { ChevronRight } from "lucide-react";
import { clearOwnedAssistState, useOwnedAssistState } from '@/features/coach/assist/ownedAssistState';
import { useRoster } from "@/features/coach/hooks/useRoster";
import {
  draftContextKey,
  isCurrentDraftContext,
  useComposerDraftContext,
  type DraftContext,
} from "@/features/coach-review/composerDrafts";
import { draftJobMessage, publicationVisibility } from '@/features/coach/assist/assistPresentation';
import { ApiError, errorMessage } from "@/lib/api";
import type {
  ApproveConfirmedCoachingDraftRequest,
  CoachingApprovalFields,
  CoachingApplicationDraft,
  CoachingAIJobProgress,
  CoachingConfirmationResponse,
  CoachingPublicationPreview,
  CoachingPublicationSelection,
  CoachingVideoReference,
  CreateCoachingConfirmationRequest,
  DiscardCoachingCaptureRequest,
  GenerateConfirmedCoachingDraftRequest,
} from "@/lib/types";
import { ProposalEvidence } from "./ProposalEvidence";
import { useDraftInbox } from "./useDraftInbox";
import {
  boundedHumanEdits,
  clearReviewCarryForOrigin,
  clearReviewCarryForCapture,
  FrozenOperation,
  proposedWriting,
  readReviewCarry,
  recipientsReviewable,
  saveReviewCarry,
} from "./reviewState";

export function DraftInbox({
  onManualFeedback,
  visible = true,
  embedded = false,
  captureId,
  selectionIntent = 0,
}: {
  onManualFeedback: () => void;
  visible?: boolean;
  embedded?: boolean;
  captureId?: string | null;
  selectionIntent?: number;
}) {
  const owner = useComposerDraftContext();
  return owner ? (
    <OwnedInbox
      key={draftContextKey(owner)}
      owner={owner}
      onManualFeedback={onManualFeedback}
      visible={visible}
      embedded={embedded}
      requestedCaptureId={captureId}
      selectionIntent={selectionIntent}
    />
  ) : null;
}
type ApprovalOperation = {
  captureId: string;
  draftId: string;
  body: ApproveConfirmedCoachingDraftRequest;
  metricLabels?: EditCategory[] | null;
};
function approvalOperationKey(request: ApprovalOperation): string {
  return JSON.stringify([
    request.captureId,
    request.draftId,
    request.body.client_operation_id,
  ]);
}
function sameApprovalOperation(
  left: ApprovalOperation | null,
  right: ApprovalOperation,
): boolean {
  return !!left && approvalOperationKey(left) === approvalOperationKey(right);
}
function OwnedInbox({
  owner,
  onManualFeedback,
  visible,
  embedded,
  requestedCaptureId,
  selectionIntent,
}: {
  owner: DraftContext;
  onManualFeedback: () => void;
  visible: boolean;
  embedded: boolean;
  requestedCaptureId?: string | null;
  selectionIntent: number;
}) {
  const [open, setOpen] = useState(embedded);
  const [publishedReceipt, setPublishedReceipt] = useState<string | null>(null);
  const [pendingApproval] = useOwnedAssistState(owner, 'inbox:approval-pending', { current: null as ApprovalOperation | null });
  const [pendingApprovals] = useOwnedAssistState(owner, 'inbox:approval-receipts', { current: new Map<string, ApprovalOperation>() });
  const [captureId, setCaptureId] = useState<string | null>(
    () => requestedCaptureId ?? pendingApproval.current?.captureId ?? (embedded ? null : readReviewCarry(owner)?.origin.captureId ?? null),
  );
  const [reviewOpen, setReviewOpen] = useState(!!captureId);
  const [visited, setVisited] = useState<string[]>(captureId ? [captureId] : []);
  const flow = useDraftInbox(owner, null, visible && open);
  const effort = useAuthoringEffort(owner, visible && open && reviewOpen && !!captureId);
  const roster = useRoster();
  const [reconciliationError, setReconciliationError] = useState<{
    operationKey: string;
    message: string;
  } | null>(null);
  const [reconcilingOperationKey, setReconcilingOperationKey] = useState<
    string | null
  >(null);
  const lifetime = useRef(true);
  const selectedItem = flow.inbox.data?.items.find(item => item.capture_id === captureId);
  const availableCaptureIds = useRef(new Set<string>());
  availableCaptureIds.current = new Set(flow.inbox.data?.items.map(item => item.capture_id) ?? []);
  useEffect(() => {
    lifetime.current = true;
    return () => {
      lifetime.current = false;
    };
  }, []);
  const recordReceipt = (request: ApprovalOperation, result: unknown) => {
    if (!lifetime.current || !isCurrentDraftContext(owner)) return;
    const receipt = result as import("@/lib/types").CoachingApprovalResponse;
    clearReviewCarryForOrigin(owner, request);
    pendingApprovals.current.delete(approvalOperationKey(request));
    setVisited(previous => previous.filter(id => id !== request.captureId));
    if (sameApprovalOperation(pendingApproval.current, request)) {
      pendingApproval.current = null;
      setReconciliationError((current) =>
        current?.operationKey === approvalOperationKey(request) ? null : current,
      );
    }
    setPublishedReceipt(
      `Published ${receipt.artifact_kind} #${receipt.artifact_id}`,
    );
    setCaptureId((current) => (current === request.captureId ? null : current));
  };
  const observeApproval = (
    request: ApprovalOperation,
    work: () => Promise<unknown>,
  ) => {
    const measurement = effort.session;
    const operation = (async () => {
      await measurement?.beforeSave({ id: request.draftId, revision: request.body.expected_draft_revision }).catch(() => undefined);
      if (!lifetime.current || !isCurrentDraftContext(owner)) throw new Error("Coaching context changed");
      return work();
    })();
    pendingApproval.current = structuredClone(request);
    pendingApprovals.current.set(approvalOperationKey(request), structuredClone(request));
    void operation.then(
      (result) => {
        if (lifetime.current && isCurrentDraftContext(owner)) void measurement?.received({ event: "outcome", outcome: "approved", domain_operation_id: request.body.client_operation_id, edit_categories: request.metricLabels ?? null });
        if (!availableCaptureIds.current.has(request.captureId)) recordReceipt(request, result);
      },
      (error) => {
        measurement?.domainFailed();
        if (
          lifetime.current &&
          isCurrentDraftContext(owner) &&
          sameApprovalOperation(pendingApproval.current, request)
        )
          setReconciliationError({
            operationKey: approvalOperationKey(request),
            message: errorMessage(error),
          });
      },
    );
    return operation;
  };
  const reconcileApproval = async (request: ApprovalOperation) => {
    if (
      reconcilingOperationKey === approvalOperationKey(request) ||
      !isCurrentDraftContext(owner)
    )
      return;
    const operationKey = approvalOperationKey(request);
    setReconcilingOperationKey(operationKey);
    setReconciliationError((current) =>
      current?.operationKey === operationKey ? null : current,
    );
    try {
      const result = await flow.mutation.mutateAsync(() =>
        observeApproval(request, () => flow.approve(request.draftId, request.body)),
      );
      recordReceipt(request, result);
    } catch (error) {
      if (
        lifetime.current &&
        isCurrentDraftContext(owner) &&
        pendingApprovals.current.has(approvalOperationKey(request))
      )
        setReconciliationError({ operationKey, message: errorMessage(error) });
    } finally {
      if (lifetime.current && isCurrentDraftContext(owner))
        setReconcilingOperationKey((current) =>
          current === operationKey ? null : current,
        );
    }
  };
  useEffect(() => {
    if (captureId) setOpen(true);
  }, [captureId]);
  useEffect(() => {
    if (!requestedCaptureId) return;
    setCaptureId(requestedCaptureId);
    setReviewOpen(true);
    setOpen(true);
  }, [requestedCaptureId, selectionIntent]);
  useEffect(() => {
    if (captureId) setVisited(previous => previous.includes(captureId) ? previous : [...previous, captureId]);
  }, [captureId]);
  const items = flow.inbox.data?.items ?? [];
  return (
    <section
      className={embedded ? "min-w-0" : "min-w-0 rounded-panel border border-rule bg-surface p-3"}
      aria-label="Private draft inbox"
    >
      <div className="flex flex-wrap items-center gap-2">
        {!embedded && <Button
          variant="ghost"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          Private draft inbox ({items.length})
        </Button>}
        <span className="text-xs text-ink-muted">
          Only your unfinished captures. Nothing is shared until approval.
        </span>
      </div>
      {publishedReceipt && <p role="status">{publishedReceipt}</p>}
      {effort.session && !selectedItem && <AuthoringEffortControls effort={effort} input={() => ({ task_kind: effort.session!.start.task_kind, mode: "assisted", player_ids: [] })} />}
      {open && (
        <div className="mt-3 grid min-w-0 gap-3">
          {flow.inbox.isLoading && (
            <p role="status">Loading private captures…</p>
          )}
          {flow.inbox.error && (
            <p role="alert">
              Inbox unavailable.{" "}
              <Button variant="ghost" onClick={() => void flow.inbox.refetch()}>
                Retry
              </Button>
            </p>
          )}
          {!flow.inbox.isLoading && !flow.inbox.error && !items.length && (
            <p className="text-sm text-ink-muted">No unfinished captures.</p>
          )}
          <div className="w-full min-w-0 divide-y divide-rule border-y border-rule">
            {items.map((item) => (
              <Button
                key={item.capture_id}
                data-testid={`draft-inbox-capture-${item.capture_id}`}
                variant="ghost"
                className="draft-inbox-row h-auto min-h-11 w-full min-w-0 justify-between whitespace-normal break-words rounded-none border-0 px-0 py-4 leading-normal text-left"
                onClick={() => { if (captureId !== item.capture_id) effort.reset(); setCaptureId(item.capture_id); setReviewOpen(true); }}
              >
                <span className="min-w-0">
                {item.kind === "marker" ? "Silent marker" : "Saved observation"}{" "}
                <span className="block text-xs font-normal text-ink-muted">{new Date(item.received_at).toLocaleString()} · {captureStatus(item.status)}</span>
                </span><ChevronRight aria-hidden="true" className="ml-3 size-4 shrink-0" />
              </Button>
            ))}
          </div>
          {visited.map(id => <RetainedCaptureReview
              key={`${id}:${owner.generation}`}
              owner={owner}
              captureId={id}
              active={visible && reviewOpen && id === captureId}
              players={roster.data?.players ?? []}
              onClose={() => {
                setReviewOpen(false);
              }}
              observeApproval={observeApproval}
              effort={effort}
              onManualFeedback={onManualFeedback}
              onPublished={(message, request) => {
                pendingApprovals.current.delete(approvalOperationKey(request));
                setVisited(previous => previous.filter(id => id !== request.captureId));
                if (sameApprovalOperation(pendingApproval.current, request))
                  pendingApproval.current = null;
                setReconciliationError((current) =>
                  current?.operationKey === approvalOperationKey(request)
                    ? null
                    : current,
                );
                setPublishedReceipt(message);
                setCaptureId((current) =>
                  current === request.captureId ? null : current,
                );
              }}
            />)}
          {Array.from(pendingApprovals.current.values()).filter(request => !items.some(item => item.capture_id === request.captureId)).map(request => (
            <div key={approvalOperationKey(request)} className="grid gap-2 border-t border-rule pt-3" role="status" data-testid={`draft-inbox-reconcile-${request.captureId}`}>
              <p>
                Publication acknowledgment is unresolved. Current source and
                proposal are unavailable.
              </p>
              {reconciliationError?.operationKey ===
                approvalOperationKey(request) && (
                <p role="alert">{reconciliationError.message}</p>
              )}
              <Button
                disabled={
                  reconcilingOperationKey ===
                  approvalOperationKey(request)
                }
                onClick={() => void reconcileApproval(request)}
              >
                Retry unchanged operation
              </Button>
            </div>
          ))}
          {captureId &&
            !selectedItem &&
            !pendingApproval.current &&
            !flow.inbox.isLoading && (
              <p role="status">
                This capture is no longer available in your inbox.{" "}
                <Button
                  variant="ghost"
                  onClick={() => {
                    setCaptureId(null);
                    setReviewOpen(false);
                  }}
                >
                  Close
                </Button>
              </p>
            )}
          {(!reviewOpen || !selectedItem) && <Button variant="ghost" onClick={onManualFeedback}>
            Write feedback manually
          </Button>}
        </div>
      )}
    </section>
  );
}
function captureStatus(status: import('@/lib/types').CoachingCapture['status']): string {
  return { received: 'Private capture · confirm facts and recipients', queued: 'AI draft queued', processing: 'AI drafting in progress', needs_review: 'Private AI draft · review before approval', approved: 'Approved publication', discarded: 'Capture discarded', blocked: 'Processing blocked · manual feedback available', retryable: 'Request needs attention · review before retrying' }[status];
}
function RetainedCaptureReview({ captureId, active, ...props }: Omit<React.ComponentProps<typeof CaptureReview>, 'flow'> & { captureId: string; active: boolean }) {
  const flow = useDraftInbox(props.owner, captureId, active);
  if (!flow.item) return null;
  return <CaptureReview {...props} flow={flow} active={active} />;
}
function CaptureReview({
  active,
  owner,
  flow,
  players,
  onClose,
  onPublished,
  observeApproval,
  effort,
  onManualFeedback,
}: {
  active: boolean;
  owner: DraftContext;
  flow: ReturnType<typeof useDraftInbox>;
  players: import("@/lib/types").Player[];
  onClose: () => void;
  onPublished: (message: string, request: ApprovalOperation) => void;
  observeApproval: (
    request: ApprovalOperation,
    work: () => Promise<unknown>,
  ) => Promise<unknown>;
  effort: AuthoringEffort;
  onManualFeedback: () => void;
}) {
  const item = flow.item!;
  const [portal, setPortal] = useState<HTMLDivElement | null>(null);
  const namespace = `inbox:${item.capture_id}`;
  const [selection, setSelection] = useOwnedAssistState<CoachingPublicationSelection>(owner, `${namespace}:selection`, {
    kind: "feedback",
    publication_class: "player",
    player_ids: [],
  });
  const [preview, setPreview] = useState<CoachingPublicationPreview | null>(
    null,
  );
  const [editingConfirmation, setEditingConfirmation] = useOwnedAssistState(owner, `${namespace}:editing-confirmation`, false);
  const currentConfirmation =
    item.source_revision &&
    !item.confirmation_code &&
    !flow.confirmation.error &&
    item.confirmation &&
    flow.confirmation.data?.id === item.confirmation.id &&
    flow.confirmation.data.revision === item.confirmation.revision
      ? flow.confirmation.data
      : null;
  const confirmation = editingConfirmation ? null : currentConfirmation;
  const [reviewed, setReviewed] = useState(false);
  const [factDraft, setFactDraft] = useOwnedAssistState<ConfirmationForm>(owner, `${namespace}:fact`, {
    statement: '', basis: 'reported', relationship: 'unknown', observerId: null, precision: 'unknown', occurrence: '',
  });
  const factForm = useForm<ConfirmationForm>({
    resolver: zodResolver(confirmationFactSchema),
    defaultValues: factDraft,
  });
  const statement = factForm.watch("statement"),
    basis = factForm.watch("basis"),
    relationship = factForm.watch("relationship"),
    date = factForm.watch("occurrence"),
    precision = factForm.watch("precision"),
    observerId = factForm.watch("observerId");
  useEffect(() => {
    setFactDraft({ statement, basis, relationship, occurrence: date, precision, observerId });
  }, [statement, basis, relationship, date, precision, observerId]);
  const setStatement = (value: string) =>
    factForm.setValue("statement", value, { shouldDirty: true });
  const setBasis = (value: ConfirmationForm["basis"]) =>
    factForm.setValue("basis", value, { shouldDirty: true });
  const setRelationship = (value: ConfirmationForm["relationship"]) =>
    factForm.setValue("relationship", value, { shouldDirty: true });
  const setObserverId = (value: string | null) =>
    factForm.setValue("observerId", value, { shouldDirty: true });
  const setPrecision = (value: ConfirmationForm["precision"]) =>
    factForm.setValue("precision", value, { shouldDirty: true });
  const setDate = (value: string) =>
    factForm.setValue("occurrence", value, { shouldDirty: true });
  const me = useAuthStore((s) => s.me);
  const selfRoles =
    me?.memberships
      .filter(
        (m) =>
          m.team_id === owner.teamId &&
          ["coach", "assistant_coach"].includes(m.role),
      )
      .map((m) => m.role) ?? [];
  const observers = [
    {
      value: "unknown",
      label: "Observer unknown",
      id: null,
      relationship: "unknown" as const,
    },
    ...selfRoles.map((role) => ({
      value: `${owner.actorId}:${role}`,
      label: `${me?.user.display_name || "Me"} · ${role.replace("_", " ")}`,
      id: owner.actorId,
      relationship: role as "coach" | "assistant_coach",
    })),
    ...players
      .filter((p) => selection.player_ids.includes(p.id))
      .flatMap((p) =>
        p.links
          .filter(
            (link) =>
              link.relationship === "guardian" || link.relationship === "self",
          )
          .map((link) => ({
            value: `${link.user_id}:${link.relationship}`,
            label: `${link.display_name || "Name unavailable"} · ${link.relationship}`,
            id: link.user_id,
            relationship: link.relationship as "guardian" | "self",
          })),
      ),
  ];
  const validObservers = observers.filter(
    (o, i, list) =>
      list.findIndex((x) => x.value === o.value) === i &&
      (basis === "reported" ||
        ["coach", "assistant_coach"].includes(o.relationship)),
  );
  const [admittedJob, setAdmittedJob] = useOwnedAssistState<CoachingAIJobProgress | null>(owner, `${namespace}:job`, null);
  const currentJob = item.job ?? admittedJob;
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const [receipt, setReceipt] = useState<string | null>(null);
  const [confirmationOp] = useOwnedAssistState(owner, `${namespace}:confirm-operation`, { current: new FrozenOperation<CreateCoachingConfirmationRequest>() });
  const [generationOp] = useOwnedAssistState(owner, `${namespace}:generate-operation`, { current: new FrozenOperation<{
      id: string;
      body: GenerateConfirmedCoachingDraftRequest;
    }>() });
  const [approvalOp] = useOwnedAssistState(owner, `${namespace}:approve-operation`, { current: new FrozenOperation<{
      captureId: string;
      draftId: string;
      body: ApproveConfirmedCoachingDraftRequest;
    }>() });
  const [discardOp] = useOwnedAssistState(owner, `${namespace}:discard-operation`, { current: new FrozenOperation<DiscardCoachingCaptureRequest>() });
  const [awaiting, setAwaiting] = useOwnedAssistState<
    "confirmation" | "generate" | "approve" | "discard" | null
  >(owner, `${namespace}:awaiting`, null);
  const alive = useRef(true);
  const currentSource = useRef({
    id: item.capture_id,
    revision: item.source_revision,
  });
  currentSource.current = {
    id: item.capture_id,
    revision: item.source_revision,
  };
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    setPreview(null);
    setReviewed(false);
    setAdmittedJob(null);
  }, [item.source_revision]);
  const attempt = async (
    kind: typeof awaiting,
    work: () => Promise<unknown>,
    settle?: (result: unknown) => void,
  ) => {
    if (inFlight.current || !isCurrentDraftContext(owner)) return;
    const dispatchedSource = { ...currentSource.current };
    inFlight.current = true;
    setAwaiting(kind);
    setBusy(true);
    setError("");
    try {
      const result = await flow.mutation.mutateAsync(work);
      if (alive.current && isCurrentDraftContext(owner)) {
        if (
          currentSource.current.id !== dispatchedSource.id ||
          currentSource.current.revision !== dispatchedSource.revision
        ) {
          setError(
            "Source changed while this request was pending. Refresh the current review.",
          );
          setAwaiting(kind);
          setPreview(null);
          setReviewed(false);
          return;
        }
        setAwaiting(null);
        settle?.(result);
      }
    } catch (e) {
      if (alive.current && isCurrentDraftContext(owner)) {
        setError(errorMessage(e));
        setAwaiting(kind);
        setPreview(null);
        setReviewed(false);
        void flow.source.refetch();
        void flow.confirmation.refetch();
        void flow.draft.refetch();
      }
    } finally {
      inFlight.current = false;
      if (alive.current && isCurrentDraftContext(owner)) setBusy(false);
    }
  };
  const sourcePermitted =
    !!item.source_revision &&
    flow.source.data?.capture.revision === item.revision &&
    !!flow.source.data &&
    !flow.source.error &&
    !flow.inbox.error;
  useEffect(() => {
    if (
      !item.source_revision ||
      [
        flow.source.error,
        flow.inbox.error,
        flow.confirmation.error,
        flow.draft.error,
      ].some((e) => e instanceof ApiError && [401, 403, 404].includes(e.status))
    )
      clearReviewCarryForCapture(owner, item.capture_id);
  }, [
    flow.source.error,
    flow.inbox.error,
    item.source_revision,
    flow.confirmation.error,
    flow.draft.error,
    owner,
  ]);
  const previewAudience = preview ?? confirmation;
  const audienceOK =
    !!previewAudience &&
    (!preview || preview.capture_source_revision === item.source_revision) &&
    previewAudience.selection.player_ids.every((id) =>
      players.some((p) => p.id === id),
    ) &&
    recipientsReviewable(
      previewAudience.recipients,
      previewAudience.recipient_user_ids,
    );
  useEffect(() => {
    setPreview(null);
    setReviewed(false);
  }, [currentConfirmation?.preview_revision]);
  const refreshAudience = () =>
    void attempt(
      null,
      () => flow.preview(item.capture_id, confirmation?.selection ?? selection),
      (result) => {
        setPreview(result as CoachingPublicationPreview);
        setReviewed(false);
      },
    );
  const confirm = () => {
    if (
      !preview ||
      !reviewed ||
      !audienceOK ||
      !statement.trim() ||
      !sourcePermitted
    )
      return;
    const request = confirmationOp.current.begin(() => ({
      client_operation_id: crypto.randomUUID(),
      expected_capture_revision: preview.capture_revision,
      expected_confirmation_revision: preview.confirmation_revision,
      preview_revision: preview.preview_revision,
      recipient_user_ids: preview.recipient_user_ids,
      selection: preview.selection,
      fact: humanConfirmationFact(factForm.getValues()),
    }));
    void attempt(
      "confirmation",
      () => flow.confirm(item.capture_id, request),
      () => {
        setEditingConfirmation(false);
        confirmationOp.current.reset();
      },
    );
  };
  const generate = () => {
    if (!confirmation || !sourcePermitted) return;
    const request = generationOp.current.begin(() => ({
      id: confirmation.id,
      body: {
        client_operation_id: crypto.randomUUID(),
        expected_confirmation_revision: confirmation.revision,
      },
    }));
    void attempt(
      "generate",
      () => flow.generate(request.id, request.body),
      (result) => {
        setAdmittedJob(result as CoachingAIJobProgress);
        generationOp.current.reset();
      },
    );
  };
  const [metricLabels, setMetricLabels] = useState<EditCategory[] | null>(null);
  const discard = () => {
    const request = discardOp.current.begin(() => ({
      client_operation_id: crypto.randomUUID(),
      expected_revision: item.revision,
      status: "discarded",
    }));
    void attempt(
      "discard",
      async () => {
        const measurement = effort.session;
        if (flow.draft.data?.draft.schema_version === "coaching-draft.v3") await measurement?.beforeSave({ id: flow.draft.data.draft.id, revision: flow.draft.data.draft.revision }).catch(() => undefined);
        if (!isCurrentDraftContext(owner)) throw new Error("Coaching context changed");
        let result: unknown;
        try { result = await flow.discard(item.capture_id, request); } catch (error) { measurement?.domainFailed(); throw error; }
        void measurement?.received({ event: "outcome", outcome: "discarded", domain_operation_id: request.client_operation_id });
        return result;
      },
      () => {
        clearReviewCarryForCapture(owner, item.capture_id);
        clearOwnedAssistState(owner, namespace);
        onClose();
      },
    );
  };
  const draft =
    flow.draft.error || item.draft_code || !sourcePermitted || !confirmation
      ? undefined
      : flow.draft.data?.draft;
  const published = (request: ApprovalOperation, result: unknown) => {
    const r = result as import("@/lib/types").CoachingApprovalResponse;
    clearReviewCarryForOrigin(owner, request);
    clearOwnedAssistState(owner, namespace);
    setReceipt(`Published ${r.artifact_kind} #${r.artifact_id}`);
    onPublished(`Published ${r.artifact_kind} #${r.artifact_id}`, request);
    approvalOp.current.reset();
  };
  const approve = (
    fields: CoachingApprovalFields,
    video?: CoachingVideoReference,
  ) => {
    if (
      !draft ||
      !confirmation ||
      !preview ||
      !reviewed ||
      !audienceOK ||
      !sourcePermitted
    )
      return;
    const request = approvalOp.current.begin(() => ({
      captureId: item.capture_id,
      draftId: draft.id,
      metricLabels: metricLabels === null ? null : [...metricLabels],
      body: {
        client_operation_id: crypto.randomUUID(),
        expected_draft_revision: draft.revision,
        confirmation_id: confirmation.id,
        expected_confirmation_revision: confirmation.revision,
        preview_revision: preview.preview_revision,
        recipient_user_ids: preview.recipient_user_ids,
        selection: preview.selection,
        fields,
        ...(video ? { video_reference: video } : {}),
      },
    }));
    void attempt(
      "approve",
      () =>
        observeApproval(request, () => flow.approve(request.draftId, request.body)),
      (result) => published(request, result),
    );
  };
  // The heading follows the same permitted review state as the form, including
  // a return to fact editing. Cached query objects alone do not establish a step.
  const reviewStep = receipt
    ? "Publication complete"
    : !sourcePermitted
      ? "Review paused · current source unavailable"
      : !confirmation
        ? "1. Check facts and recipients"
        : draft?.schema_version === "coaching-draft.v3" && draft.status === "needs_review"
          ? "3. Review and approve"
          : "2. Draft with AI";
  return (
    <CoachModal title="Review saved observation" size="wide" active={active} onClose={onClose} description={reviewStep}>
    {receipt ? <p role="status">
      {receipt}
      <Button variant="ghost" onClick={onClose}>Close</Button>
    </p> : <div
      ref={setPortal}
      onChangeCapture={() => queueMicrotask(() => setFactDraft(factForm.getValues()))}
      onClickCapture={event => {
        if ((busy || awaiting) && event.target instanceof Element && event.target.closest('a[href]')) {
          event.preventDefault(); event.stopPropagation();
        }
      }}
      className="grid min-w-0 grid-cols-1 gap-3 break-words border-t border-rule pt-3"
      data-testid="draft-inbox-review"
    >
      <AuthoringEffortControls effort={effort} labels={metricLabels} onLabels={setMetricLabels} input={() => ({ task_kind: (confirmation?.selection ?? selection).kind === "action" ? "goal" : "feedback", mode: "assisted", player_ids: (confirmation?.selection ?? selection).player_ids, origin: { kind: "capture", source_id: item.capture_id, source_revision: item.revision } })} />
      <div className="flex min-w-0 flex-wrap gap-2">
        <Button variant="ghost" disabled={busy || !!awaiting || ['queued', 'processing'].includes(item.status)} onClick={discard}>
          Discard private capture
        </Button>
        <Button variant="outline" onClick={onManualFeedback}>Write feedback manually</Button>
      </div>
      {['queued', 'processing'].includes(item.status) && <p className="text-sm text-ink-muted">AI drafting is in progress. Nothing is published; discard becomes available when processing finishes.</p>}
      {flow.inbox.error && <p role="alert" className="text-sm">Current draft status could not be refreshed. Your work is retained.
        <Button type="button" variant="outline" disabled={flow.inbox.isFetching} onClick={() => void flow.inbox.refetch()}>Check draft status</Button>
      </p>}
      {flow.source.isLoading && <p role="status">Loading permitted source…</p>}
      {!sourcePermitted && (
        <p role="alert">
          Source unavailable. Review and publication are paused.
          <Button type="button" variant="ghost" onClick={() => void flow.source.refetch()}>Reload source</Button>
        </p>
      )}
      {sourcePermitted && (
        <section>
          <h4 className="text-sm font-display">Original private capture</h4>
          <p className="whitespace-pre-wrap text-sm">
            {flow.source.data!.capture.text ??
              "Silent marker: supply your own factual statement."}
          </p>
          <p className="text-xs text-ink-muted">
            Recorded {new Date(item.occurred_at).toLocaleString()} · Received{" "}
            {new Date(item.received_at).toLocaleString()} · Expires{" "}
            {new Date(item.expires_at).toLocaleString()}. Capture time does not
            establish fact occurrence. Player hints remain unresolved.
          </p>
        </section>
      )}
      {error && <p role="alert">{error}</p>}
      {awaiting && (
        <div className="flex flex-wrap gap-2">
          <p className="text-sm">
            Acknowledgment is unresolved. Retry the exact frozen operation.
          </p>
          <Button
            disabled={busy}
            onClick={() => {
              const op = awaiting;
              if (op === "confirmation") {
                const r = confirmationOp.current.begin(() => {
                  throw new Error("Missing request");
                });
                void attempt(
                  op,
                  () => flow.confirm(item.capture_id, r),
                  () => {
                    setEditingConfirmation(false);
                    confirmationOp.current.reset();
                  },
                );
              } else if (op === "generate") {
                const r = generationOp.current.begin(() => {
                  throw new Error("Missing request");
                });
                void attempt(
                  op,
                  () => flow.generate(r.id, r.body),
                  (result) => {
                    setAdmittedJob(result as CoachingAIJobProgress);
                    generationOp.current.reset();
                  },
                );
              } else if (op === "discard") discard();
              else {
                const r = approvalOp.current.begin(() => {
                  throw new Error("Missing request");
                });
                const request: ApprovalOperation = r;
                void attempt(
                  op,
                  () => observeApproval(request, () => flow.approve(request.draftId, request.body)),
                  (result) => published(request, result),
                );
              }
            }}
          >
            Retry unchanged operation
          </Button>
          <Button
            variant="outline"
            disabled={busy}
            onClick={() => {
              confirmationOp.current.reset();
              generationOp.current.reset();
              approvalOp.current.reset();
              discardOp.current.reset();
              setAwaiting(null);
              setReviewed(false);
            }}
          >
            Begin a new review operation
          </Button>
        </div>
      )}
      {!confirmation && sourcePermitted && (
        <fieldset disabled={busy || !!awaiting} className="grid gap-3">
          <TkticSelect
            portalContainer={portal}
            ariaLabel="Publication artifact"
            disabled={busy || !!awaiting}
            value={selection.kind}
            onChange={(kind) => {
              setSelection({
                kind: kind as "feedback" | "action",
                publication_class: "player",
                player_ids: [],
              });
              setPreview(null);
              setReviewed(false);
            }}
            options={[
              { value: "feedback", label: "Feedback" },
              { value: "action", label: "Single-player action" },
            ]}
          />
          <p className="text-sm">
            Resolve the real player{selection.kind === "feedback" ? "s" : ""}{" "}
            explicitly.
          </p>
          <ComposerPlayerPicker
            players={players}
            value={selection.player_ids}
            onChange={(player_ids) => {
              setSelection({
                ...selection,
                player_ids:
                  selection.kind === "action"
                    ? player_ids.slice(-1)
                    : player_ids,
              });
              setPreview(null);
              setReviewed(false);
            }}
          />
          <TkticSelect
            portalContainer={portal}
            ariaLabel="Publication audience"
            disabled={busy || !!awaiting}
            value={selection.publication_class}
            onChange={(publication_class) => {
              setSelection({
                ...selection,
                publication_class:
                  publication_class as CoachingPublicationSelection["publication_class"],
              });
              setPreview(null);
              setReviewed(false);
            }}
            options={[
              { value: "creator_private", label: "Only me" },
              {
                value: "player",
                label: "Selected players and their linked family",
              },
              ...(selection.kind === "feedback"
                ? [{ value: "team", label: "Team audience" }]
                : []),
            ]}
          />
          <label className="text-sm">
            Human factual statement (separate from proposed wording)
            <textarea
              value={statement}
              maxLength={990}
              onChange={(e) => setStatement(e.target.value)}
              className="mt-1 w-full rounded-control border border-rule bg-canvas p-2"
            />
          </label>
          <TkticSelect
            portalContainer={portal}
            ariaLabel="Factual basis"
            disabled={busy || !!awaiting}
            value={basis}
            onChange={(v) => {
              setBasis(v as typeof basis);
              setRelationship("unknown");
              setObserverId(null);
            }}
            options={[
              { value: "reported", label: "Reported to me" },
              { value: "observed", label: "Personally observed" },
            ]}
          />
          <TkticSelect
            portalContainer={portal}
            ariaLabel="Actual observer"
            disabled={busy || !!awaiting}
            value={
              relationship === "unknown"
                ? "unknown"
                : `${observerId}:${relationship}`
            }
            onChange={(value) => {
              const selected = validObservers.find((o) => o.value === value);
              if (selected) {
                setObserverId(selected.id);
                setRelationship(selected.relationship);
              }
            }}
            options={validObservers.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
            placeholder="Select the actual observer"
          />
          <TkticSelect
            portalContainer={portal}
            ariaLabel="Occurrence precision"
            disabled={busy || !!awaiting}
            value={precision}
            onChange={(v) => {
              setPrecision(v as typeof precision);
              setDate("");
            }}
            options={[
              { value: "unknown", label: "Occurrence unknown" },
              { value: "day", label: "Known day" },
              { value: "instant", label: "Known instant with timezone" },
            ]}
          />
          {precision === "day" ? (
            <label className="text-sm">
              Actual occurrence day
              <TkticDateInput value={date} onChange={setDate} />
            </label>
          ) : precision === "instant" ? (
            <label className="text-sm">
              Actual occurrence instant with timezone
              <input
                value={date}
                onChange={(e) => setDate(e.target.value)}
                placeholder="2026-10-07T15:00:00Z"
                className="mt-1 w-full rounded-control border border-rule bg-canvas p-2"
              />
            </label>
          ) : (
            <p className="text-sm text-ink-muted">
              Occurrence stays unknown; capture time is not substituted.
            </p>
          )}
        </fieldset>
      )}
      {confirmation && sourcePermitted && (
        <section>
          <h4 className="font-display">Confirmed human fact</h4>
          <p className="whitespace-pre-wrap text-sm">
            {confirmation.fact.statement}
          </p>
          <p className="text-xs text-ink-muted">
            {confirmation.fact.basis} ·{" "}
            {confirmation.fact.observer_relationship} ·{" "}
            {confirmation.fact.occurrence_precision === "unknown"
              ? "Occurrence unknown"
              : confirmation.fact.occurred_at}{" "}
            · Reviewed by you. Final wording does not change this fact.
          </p>
        </section>
      )}
      {confirmation && (
        <Button
          variant="outline"
          disabled={busy || !!awaiting}
          onClick={() => {
            setSelection(confirmation.selection);
            setStatement(confirmation.fact.statement);
            setBasis(confirmation.fact.basis);
            setRelationship(
              confirmation.fact.observer_user_id === owner.actorId
                ? confirmation.fact.observer_relationship
                : "unknown",
            );
            setObserverId(confirmation.fact.observer_user_id ?? null);
            setRelationship(confirmation.fact.observer_relationship);
            setPrecision(confirmation.fact.occurrence_precision);
            setDate(confirmation.fact.occurred_at ?? "");
            setEditingConfirmation(true);
            setPreview(null);
            setReviewed(false);
          }}
        >
          Change confirmed evidence or selection
        </Button>
      )}
      <Button
        variant="outline"
        disabled={
          busy ||
          !!awaiting ||
          !sourcePermitted ||
          (!confirmation && !selection.player_ids.length)
        }
        onClick={refreshAudience}
      >
        Refresh complete recipient preview
      </Button>
      {previewAudience && sourcePermitted && (
        <section>
          <h4 className="font-display">Complete publication audience</h4>
          <p className="text-sm">
            {publicationVisibility(previewAudience.selection.publication_class)} ·{" "}
            {previewAudience.selection.player_ids
              .map(
                (id) =>
                  players.find((p) => p.id === id)?.display_name ??
                  "Player name unavailable",
              )
              .join(", ")}
          </p>
          {previewAudience.recipients.length ? (
            <ul>
              {previewAudience.recipients.map((r) => (
                <li key={r.user_id} className="text-sm">
                  {r.label_status === "available" && r.display_name
                    ? r.display_name
                    : "Recipient name unavailable — review incomplete"}{" "}
                  · {r.relationships.join(", ")}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm">Only you. No player or family recipients.</p>
          )}
          <Button
            variant={reviewed ? "primary" : "outline"}
            disabled={!preview || !audienceOK || busy || !!awaiting}
            onClick={() => setReviewed((v) => !v)}
            aria-pressed={reviewed}
          >
            {reviewed
              ? "Audience reviewed"
              : "I reviewed every actual recipient and resolved player"}
          </Button>
        </section>
      )}
      {!confirmation && <p className="text-xs text-ink-muted">Confirmation saves your facts and recipients. No AI call; nothing shared.</p>}
      {!confirmation && (
        <Button
          disabled={
            !reviewed ||
            !preview ||
            !statement.trim() ||
            busy ||
            !!awaiting ||
            !sourcePermitted ||
            !confirmationFactSchema.safeParse(factForm.getValues()).success ||
            (relationship !== "unknown" &&
              !validObservers.some(
                (o) => o.id === observerId && o.relationship === relationship,
              ))
          }
          onClick={confirm}
        >
          {busy && awaiting === "confirmation" ? "Confirming facts and recipients…" : "Confirm facts and recipients"}
        </Button>
      )}
      {confirmation && !draft && (
        <>
          <Button
            disabled={
              busy ||
              !!awaiting ||
              !sourcePermitted ||
              (!!currentJob &&
                (!currentJob.can_regenerate ||
                  ["queued", "processing"].includes(currentJob.status)))
            }
            onClick={generate}
          >
            {busy && awaiting === 'generate' || currentJob?.status === 'processing' ? `Drafting ${confirmation.selection.kind}…` : currentJob?.status === 'queued' ? 'AI draft queued…' : `Draft ${confirmation.selection.kind} with AI`}
          </Button>
          {currentJob && (
            <p role="status" className="text-sm">
              {draftJobMessage(currentJob, confirmation.selection.kind)}
            </p>
          )}
          <p className="text-xs text-ink-muted">AI drafts from your confirmed observation. Review the wording before sharing.</p>
        </>
      )}
      {(flow.confirmation.error ||
        flow.draft.error ||
        item.confirmation_code ||
        item.draft_code) && (
        <p role="alert">
          Current confirmation or proposal is stale or unavailable. Refresh
          current source and audience before continuing.
        </p>
      )}
      {draft && draft.schema_version !== "coaching-draft.v3" && <p role="status">This proposal uses an older evidence version. Review current confirmation before continuing; this proposal cannot be approved.</p>}
      {draft &&
        draft.schema_version === "coaching-draft.v3" &&
        draft.status === "needs_review" &&
        confirmation && (
          <ApprovalWriting
            key={`${draft.id}:${draft.revision}:${confirmation.revision}`}
            owner={owner}
            players={players}
            draft={draft}
            confirmation={confirmation}
            audience={preview}
            operationPending={busy || !!awaiting}
            disabled={
              busy || !!awaiting || !preview || !reviewed || !audienceOK
            }
            onApprove={approve}
          />
        )}
    </div>}
    </CoachModal>
  );
}
const goalSchema = z.object({
  title: z.string().trim().min(1).max(160),
  description: z.string().max(2000),
  successCriteria: z.string().max(2000),
});
function ApprovalWriting({
  owner,
  players,
  draft,
  confirmation,
  audience,
  operationPending,
  disabled,
  onApprove,
}: {
  owner: DraftContext;
  players: import("@/lib/types").Player[];
  draft: CoachingApplicationDraft;
  confirmation: CoachingConfirmationResponse;
  audience: CoachingPublicationPreview | null;
  operationPending: boolean;
  disabled: boolean;
  onApprove: (
    fields: CoachingApprovalFields,
    video?: CoachingVideoReference,
  ) => void;
}) {
  const navigate = useNavigate();
  const composer = useRef<ComposerHandle>(null);
  const stored = useRef(readReviewCarry(owner, { captureId: draft.capture_id, draftId: draft.id }));
  const origin = {
    captureId: draft.capture_id,
    draftId: draft.id,
    draftRevision: draft.revision,
    confirmationId: confirmation.id,
    confirmationRevision: confirmation.revision,
  };
  const same =
    stored.current?.kind === draft.kind &&
    JSON.stringify(stored.current?.origin) === JSON.stringify(origin);
  const recoverable =
    stored.current?.kind === draft.kind &&
    stored.current?.origin.captureId === origin.captureId;
  const defaults = {
    ...proposedWriting(draft),
    ...(same ? stored.current?.edits : {}),
  };
  const [video, setVideo] = useState(same ? stored.current?.video : undefined);
  const [approvalPreview, setApprovalPreview] = useState<{ fields: CoachingApprovalFields; video?: CoachingVideoReference } | null>(null);
  const [retainedApplied, setRetainedApplied] = useState(false);
  const retainedEdits = useRef(stored.current?.edits ?? {});
  const goal = useForm<z.infer<typeof goalSchema>>({
    resolver: zodResolver(goalSchema),
    defaultValues: {
      title: defaults.title,
      description: defaults.description ?? "",
      successCriteria: defaults.success_criteria ?? "",
    },
  });
  const persist = () => {
    const values = goal.getValues();
    const dirty = goal.formState.dirtyFields;
    const edits = boundedHumanEdits(
        {
          ...(retainedEdits.current ?? {}),
          ...(draft.kind === "feedback"
            ? (composer.current?.getDirtyValues() ?? {})
            : {
                ...(dirty.title ? { title: values.title } : {}),
                ...(dirty.description
                  ? { description: values.description }
                  : {}),
                ...(dirty.successCriteria
                  ? { success_criteria: values.successCriteria }
                  : {}),
              }),
        },
        draft.kind,
      );
    // Passive unmount runs after the Composer ref has been detached. Keep the
    // last explicit human overlay instead of replacing it with empty values.
    retainedEdits.current = edits;
    saveReviewCarry(owner, {
      kind: draft.kind,
      origin: !same && recoverable && !retainedApplied ? stored.current!.origin : origin,
      edits,
      ...(video ? { video } : {}),
    });
  };
  const persistRef = useRef(persist);
  persistRef.current = persist;
  useEffect(() => {
    const onPageHide = () => persistRef.current();
    window.addEventListener("pagehide", onPageHide);
    return () => {
      persistRef.current();
      window.removeEventListener("pagehide", onPageHide);
    };
  }, []);
  return (
    <section
      onChangeCapture={() => queueMicrotask(() => persistRef.current())}
      className="grid min-w-0 grid-cols-1 gap-3 break-words border-t border-rule pt-3"
    >
      <h4 className="font-display">
        Proposed {draft.kind === "feedback" ? "feedback" : "action"} — edit
        before approval
      </h4>
      <p className="text-xs text-ink-muted">
        Proposal received {new Date(draft.received_at).toLocaleString()};
        expires {new Date(draft.expires_at).toLocaleString()}. Source
        references:{" "}
        {draft.source_references
          .map((r) => `${r.kind.replaceAll('_', ' ')} source`)
          .join(", ")}
      </p>
      {draft.schema_version === "coaching-draft.v3" && (
        <ProposalEvidence output={draft.structured_output} players={players} />
      )}
      {!same && recoverable && !retainedApplied && (
        <div>
          <p className="text-sm">
            A newer server revision is available. Your separately typed edits
            are retained; recording proof must be selected again.
          </p>
          <Button
            variant="outline"
            disabled={operationPending}
            onClick={() => {
              const edits = stored.current?.edits ?? {};
              setRetainedApplied(true);
              retainedEdits.current = edits;
              if (draft.kind === "feedback")
                composer.current?.reset({
                  ...proposedWriting(draft),
                  ...edits,
                });
              else {
                goal.setValue("title", edits.title ?? defaults.title, {
                  shouldDirty: true,
                });
                goal.setValue(
                  "description",
                  edits.description ?? defaults.description ?? "",
                  { shouldDirty: true },
                );
                goal.setValue(
                  "successCriteria",
                  edits.success_criteria ?? defaults.success_criteria ?? "",
                  { shouldDirty: true },
                );
              }
              saveReviewCarry(owner, { kind: draft.kind, origin, edits });
            }}
          >
            Apply my retained human edits to this revision
          </Button>
        </div>
      )}
      {draft.kind === "feedback" ? (
        <div hidden={!!approvalPreview} style={approvalPreview ? { display: 'none' } : undefined}>
          <Composer
            ref={composer}
            approvalMode
            players={[]}
            defaultValues={defaults}
            disabled={disabled}
            submitLabel="Preview and approve"
            onSubmit={(values) =>
              { persist(); setApprovalPreview({ fields: {
                  ...proposedWriting(draft),
                  ...boundedHumanEdits(values, "feedback"),
                }, video }); }
            }
          />
          {video && (
            <p className="text-sm">
              Human-selected source recording: {video.slot} at{" "}
              {video.timestamp_seconds.toFixed(1)} seconds. This does not
              establish fact occurrence.{" "}
              <Button
                variant="ghost"
                onClick={() => {
                  setVideo(undefined);
                  saveReviewCarry(owner, {
                    kind: draft.kind,
                    origin,
                    edits: boundedHumanEdits(
                      {
                        ...retainedEdits.current,
                        ...composer.current?.getDirtyValues(),
                      },
                      draft.kind,
                    ),
                  });
                }}
              >
                Remove recording reference
              </Button>
            </p>
          )}
          <Button
            variant="outline"
            disabled={operationPending}
            onClick={() => {
              persist();
              void navigate({
                to: "/coach/review",
                search: { source: "video", inbox_capture_id: origin.captureId },
              });
            }}
          >
            Add video evidence in Review
          </Button>
        </div>
      ) : (
        <form
          hidden={!!approvalPreview}
          style={approvalPreview ? { display: 'none' } : undefined}
          onSubmit={goal.handleSubmit((values) => {
            persist(); setApprovalPreview({ fields: {
              ...proposedWriting(draft),
              title: values.title,
              description: values.description,
              success_criteria: values.successCriteria,
            } });
          })}
          className="grid gap-3"
        >
          <GoalWritingFields
            title={goal.register("title")}
            description={goal.register("description")}
            successCriteria={goal.register("successCriteria")}
            errors={goal.formState.errors}
          />
          <p className="text-sm text-ink-muted">
            Actions cannot attach video. One normal fact note and one player
            goal will be created.
          </p>
          <Button disabled={disabled} type="submit">
            Preview and approve
          </Button>
        </form>
      )}
      {approvalPreview && <section className="min-w-0 space-y-4 break-words" aria-label="Review and approve">
        <h4 className="font-display">Review and approve</h4>
        <article className="rounded-panel bg-elevated p-4 space-y-3">
          <h5 className="font-display text-lg">{approvalPreview.fields.title}</h5>
          {draft.kind === 'feedback' ? <>
            <p className="whitespace-pre-wrap">{approvalPreview.fields.player_summary}</p>
            <p className="whitespace-pre-wrap text-sm">{approvalPreview.fields.body}</p>
            {(['what_happened', 'why_it_matters', 'what_to_do_next'] as const).map(field => approvalPreview.fields[field] && <p key={field} className="whitespace-pre-wrap text-sm">{approvalPreview.fields[field]}</p>)}
          </> : <><p className="whitespace-pre-wrap">{approvalPreview.fields.description}</p><p className="whitespace-pre-wrap text-sm">{approvalPreview.fields.success_criteria}</p></>}
        </article>
        <section className="border-t border-rule pt-3 space-y-2"><h5 className="font-display">Who will receive this?</h5>
          {audience && <p className="text-sm">Visibility: {publicationVisibility(audience.selection.publication_class)}</p>}
          {!audience ? <p className="text-sm">Recipients are unavailable. Refresh the complete recipient preview before approval.</p> : audience.recipients.length ? <ul className="text-sm">{audience.recipients.map(recipient => <li key={recipient.user_id}>{recipient.display_name || 'Recipient name unavailable'} · {recipient.relationships.join(', ')}</li>)}</ul> : <p className="text-sm">Only you. No player or family recipients.</p>}
        </section>
        <p className="text-xs text-ink-muted">Approval saves the reviewed wording for this audience. An action creates a factual note and one player goal; it does not mark the goal achieved.</p>
        <div className="flex flex-wrap justify-between gap-2"><Button type="button" variant="outline" onClick={() => setApprovalPreview(null)}>Back to writing</Button><Button type="button" disabled={disabled} onClick={() => onApprove(approvalPreview.fields, approvalPreview.video)}>Approve and publish once</Button></div>
      </section>}
    </section>
  );
}
